import { Component, OnInit, Input, CUSTOM_ELEMENTS_SCHEMA, Renderer2, Inject, ElementRef, AfterViewInit, NgZone, ViewChild, OnDestroy, HostListener } from '@angular/core';
import { CommonModule, Location, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { PLATFORM_ID } from '@angular/core';

// For defining latitude/longitude coordinates
interface GeoCoordinates {
  lat: number;
  lng: number;
}

// Service import - will need to be created if it doesn't exist
interface GraveDetails {
  location?: {
    latitude: number;
    longitude: number;
  };
  latitude?: number;
  longitude?: number;
}

// Define the global AR cleanup helper interface
interface ARCleanupHelper {
  activeStreams: MediaStream[];
  registerStream: (stream: MediaStream) => void;
  cleanup: () => boolean;
}

// Extend the Window interface to include our AR cleanup helper
declare global {
  interface Window {
    ARCleanup?: ARCleanupHelper;
  }
}

class GraveSearchDetailsService {
  getCurrentGraveDetails(): GraveDetails | null {
    return null; // Implement actual service or modify this based on your implementation
  }
}

@Component({
  selector: 'app-ar-map',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA], // Required for A-Frame elements
  templateUrl: './ar-map.component.html',
  styleUrl: './ar-map.component.scss'
})
export class ArMapComponent implements OnInit, OnDestroy {
  private sceneElement: HTMLElement | null = null;
  private cleanup = false;
  private previousUrl = '/';
  private routerSubscription: Subscription | null = null;
  private popStateListener: any = null;
  private isBrowser: boolean = false;

  // Store grave coordinates
  graveLatitude: number = 0;
  graveLongitude: number = 0;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Get grave coordinates from router state
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras.state) {
      const state = navigation.extras.state as { grave: GraveDetails };
      if (state.grave) {
        this.graveLatitude = state.grave.latitude || 0;
        this.graveLongitude = state.grave.longitude || 0;
        console.log('Received coordinates in AR component:', this.graveLatitude, this.graveLongitude);
      }
    }

    // Try to get the previous URL from various sources
    // First check if we have a navigationId from Angular's router state
    if (window.history.state && window.history.state.navigationId > 1) {
      this.routerSubscription = this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          // Store only non-AR view URLs
          if (event.url !== '/ar-view') {
            this.previousUrl = event.url;
          }
          this.routerSubscription?.unsubscribe();
        }
      });
    }

    // Also check document.referrer as a fallback
    if (this.document.referrer && this.document.referrer.includes(window.location.origin)) {
      // Extract the path from the full referrer URL
      const referrerUrl = new URL(this.document.referrer);
      const path = referrerUrl.pathname;
      if (path && path !== '/ar-view') {
        this.previousUrl = path;
      }
    }

    // If URL is somehow still invalid, default to home
    if (!this.previousUrl || this.previousUrl === '/ar-view') {
      this.previousUrl = '/';
    }

    // Log for debugging
    console.log('Back navigation will go to:', this.previousUrl);
  }

  // Listen for browser back button and other navigation attempts
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: Event): void {
    console.log('Detected page unload, cleaning up AR resources');
    this.cleanupARResources();
    // Don't prevent navigation - just ensure cleanup happens
  }

  ngOnInit(): void {
    // Store a reference to the a-scene element when the component initializes
    setTimeout(() => {
      this.sceneElement = this.document.querySelector('a-scene');

      // Update the AR entity with the coordinates from navigation state
      if (this.graveLatitude && this.graveLongitude) {
        const arEntity = this.document.querySelector('a-entity[gps-entity-place]');
        if (arEntity) {
          arEntity.setAttribute('gps-entity-place', `latitude: ${this.graveLatitude}; longitude: ${this.graveLongitude};`);
          console.log('Updated AR entity with coordinates:', this.graveLatitude, this.graveLongitude);
        }
      }
    }, 100);

    // Set up browser history popstate event listener
    if (this.isBrowser) {
      this.popStateListener = this.handlePopState.bind(this);
      window.addEventListener('popstate', this.popStateListener);

      // Modify browser history to detect back button
      // This prevents the user from using back button without going through our cleanup
      history.pushState(null, '', window.location.href);
    }
  }

  // Handle the browser's back button
  private handlePopState(event: PopStateEvent): void {
    console.log('Detected browser back button, intercepting navigation');
    // Prevent default back action
    history.pushState(null, '', window.location.href);

    // Use our controlled back navigation instead
    this.goBack();
  }

  ngOnDestroy(): void {
    // Clean up subscription if it exists
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    // Remove popstate listener
    if (this.isBrowser && this.popStateListener) {
      window.removeEventListener('popstate', this.popStateListener);
    }

    // Mark that we're cleaning up to prevent additional callbacks
    this.cleanup = true;

    // Clean up AR resources
    this.cleanupARResources();
  }

  // Extract cleanup to a separate method to use in multiple places
  private cleanupARResources(): void {
    try {
      // Only stop camera streams - less aggressive approach
      if (navigator.mediaDevices) {
        const videoElements = this.document.querySelectorAll('video');
        videoElements.forEach(video => {
          if (video.srcObject) {
            try {
              const mediaStream = video.srcObject as MediaStream;
              mediaStream.getTracks().forEach(track => {
                track.stop();
              });
              video.srcObject = null;
            } catch (e) {
              console.warn('Error stopping video track:', e);
            }
          }
        });
      }

      // Stop any animations more gently
      const sceneEl = this.document.querySelector('a-scene');
      if (sceneEl) {
        try {
          // Access the AR.js system if it exists
          // @ts-ignore - A-Frame systems are not typed in TypeScript
          const arSystem = sceneEl.systems && sceneEl.systems['arjs'];
          if (arSystem && typeof arSystem.pause === 'function') {
            arSystem.pause();
          }
        } catch (e) {
          console.warn('Error pausing AR system:', e);
        }
      }

      // Use global cleanup helper if available
      if (window.ARCleanup && typeof window.ARCleanup.cleanup === 'function') {
        window.ARCleanup.cleanup();
      }
    } catch (err) {
      console.warn('Error during AR cleanup:', err);
    }
  }

  goBack(): void {
    // Clean up AR resources
    this.cleanupARResources();

    // Force navigation by setting window location
    // This is more reliable than Angular navigation for cleaning up WebGL contexts
    try {
      // Add a small delay to ensure cleanup completes
      setTimeout(() => {
        console.log('Navigating back to:', this.previousUrl);
        // Simply use window.location to force a complete page reload
        window.location.href = this.previousUrl || '/';
      }, 300);
    } catch (err) {
      console.warn('Error during navigation:', err);
      // Fallback to home page if there's an error
      window.location.href = '/';
    }
  }
}

